import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Map, Trash2, Pencil, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Regions() {
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/regions").then((r) => setRegions(r.data));
  useEffect(() => { load(); }, []);

  const del = async (e, id) => { e.stopPropagation(); await api.delete(`/regions/${id}`); toast.success("Region deleted"); load(); };
  const edit = (e, r) => { e.stopPropagation(); setEditing(r); setOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Regions</h1>
          <p className="mt-1 text-sm text-slate-500">Click a region to see its employees.</p>
        </div>
        <Button data-testid="add-region-button" onClick={() => { setEditing(null); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="mr-1.5 h-4 w-4" /> Add region
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {regions.map((r) => (
          <div key={r.id} data-testid="region-card" onClick={() => navigate(`/regions/${r.id}`)} className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white"><Map className="h-5 w-5" /></div>
              <div className="flex gap-1">
                <button onClick={(e) => edit(e, r)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                <button onClick={(e) => del(e, r.id)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-3 font-heading text-lg font-bold text-slate-900">{r.name}</p>
            <p className="text-sm text-slate-500">Region {r.number}</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-slate-400">{r.employee_count} employee{r.employee_count !== 1 ? "s" : ""}</p>
              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-orange-500" />
            </div>
          </div>
        ))}
        {regions.length === 0 && <p className="text-slate-400">No regions yet.</p>}
      </div>

      <RegionDialog open={open} onOpenChange={setOpen} region={editing} onSaved={load} />
    </div>
  );
}

export function RegionDialog({ open, onOpenChange, region, onSaved }) {
  const [form, setForm] = useState({ name: "", number: "" });
  useEffect(() => { setForm(region ? { name: region.name, number: region.number } : { name: "", number: "" }); }, [region, open]);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    if (region) await api.put(`/regions/${region.id}`, form);
    else await api.post("/regions", form);
    toast.success(region ? "Region updated" : "Region created");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="region-dialog">
        <DialogHeader><DialogTitle className="font-heading">{region ? "Edit region" : "Add region"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Region name</Label>
            <Input className="mt-1.5" data-testid="region-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Region number</Label>
            <Input className="mt-1.5" data-testid="region-number-input" value={form.number} onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="region-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
